import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, X, GripVertical, Check } from "lucide-react";
import { toast } from "sonner";

// Kept for backwards-compat with Recipes.jsx import — empty map means no
// custom colours are applied to tabs (tabs are plain text now).
export const COLOR_MAP = {};

/**
 * Custom recipe categories — TEXT ONLY (no icon, no colour) per user request.
 * Shared with the whole boat/household. Drag to reorder, X to remove.
 */
export default function RecipeCategoriesDialog({ open, onOpenChange, boatId, initialCategories = [], onSaved }) {
  const [cats, setCats] = useState([]);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCats((initialCategories || []).map((c) => ({ ...c, icon: "", color: "" })));
    }
  }, [open, initialCategories]);

  const addCategory = () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Skriv inn et navn");
      return;
    }
    const id = name.toLowerCase().replace(/\s+/g, "-").slice(0, 40);
    if (cats.some((c) => c.id === id)) {
      toast.error("En kategori med samme navn finnes allerede");
      return;
    }
    setCats((prev) => [...prev, { id, name, icon: "", color: "" }]);
    setNewName("");
  };

  const removeAt = (idx) => setCats((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (targetIdx) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const next = [...cats];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(targetIdx, 0, moved);
    setCats(next);
    setDraggedIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateBoatRecipeCategories(boatId, cats);
      toast.success(`Lagret ${cats.length} kategorier`);
      onSaved?.(cats);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre kategorier");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md max-h-[85dvh] sm:max-h-[90vh] flex flex-col p-0" data-testid="recipe-categories-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100 flex-shrink-0">
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Egne oppskriftskategorier</DialogTitle>
          <DialogDescription className="text-xs">
            Delt med hele båten/husstanden. Dra for å omrokere — X for å slette.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">
              Aktive kategorier ({cats.length})
            </label>
            {cats.length === 0 ? (
              <p className="text-sm text-stone-400 py-4 text-center">Ingen egne kategorier ennå. Legg til en under.</p>
            ) : (
              <div className="space-y-1.5" data-testid="recipe-categories-list">
                {cats.map((cat, idx) => {
                  const isOver = overIdx === idx && draggedIdx !== null && draggedIdx !== idx;
                  return (
                    <div
                      key={cat.id}
                      draggable
                      onDragStart={(e) => { setDraggedIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); }}
                      onDragLeave={() => { if (overIdx === idx) setOverIdx(null); }}
                      onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
                      onDragEnd={() => { setDraggedIdx(null); setOverIdx(null); }}
                      className={`flex items-center gap-2 rounded-lg border bg-white p-2.5 transition-all ${isOver ? "ring-2 ring-navy-500 scale-[1.01]" : ""} ${draggedIdx === idx ? "opacity-40" : ""} cursor-grab active:cursor-grabbing`}
                      data-testid={`category-row-${cat.id}`}
                    >
                      <GripVertical className="h-4 w-4 text-stone-300 flex-shrink-0" />
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-stone-100 text-stone-700 border-stone-200">
                        {cat.name}
                      </span>
                      <button
                        onClick={() => removeAt(idx)}
                        className="ml-auto p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        data-testid={`category-delete-${cat.id}`}
                        title="Fjern kategori"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-stone-100 pt-4">
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Legg til kategori</label>
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
                placeholder="t.d. Pizza, Familiemiddag, Indisk…"
                className="flex-1 min-w-0 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                data-testid="new-category-name"
              />
              <button
                onClick={addCategory}
                disabled={!newName.trim()}
                className="inline-flex items-center justify-center gap-1 rounded-md bg-navy-800 hover:bg-navy-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 flex-shrink-0"
                data-testid="add-new-category-btn"
              >
                <Plus className="h-3.5 w-3.5" /> Legg til
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-100 px-5 py-3 bg-white flex gap-2 flex-shrink-0" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-50"
            data-testid="cancel-categories-btn"
          >
            Avbryt
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-900 disabled:opacity-50"
            data-testid="save-categories-btn"
          >
            <Check className="h-4 w-4" /> {saving ? "Lagrer…" : "Lagre"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
